import { useState, useEffect, useCallback } from 'react';

type Direction = 'ltr' | 'rtl';
type Language = 'en' | 'de' | 'fr' | 'he' | 'ru' | 'ar';

const langToDirection: Record<Language, Direction> = {
  en: 'ltr', de: 'ltr', fr: 'ltr', ru: 'ltr',
  ar: 'rtl', he: 'rtl',
};

export function useDirection() {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('language') as Language | null;
    if (stored && langToDirection[stored]) return stored;
    return 'en';
  });

  const direction = langToDirection[language];

  // Apply direction and language to document
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
    localStorage.setItem('direction', direction);
    localStorage.setItem('language', language);
  }, [direction, language]);

  const setLanguage = useCallback((newLanguage: Language) => {
    setLanguageState(newLanguage);
  }, []);

  // Check if an icon should be flipped in RTL
  const shouldFlipIcon = useCallback((iconName: string): boolean => {
    const directionalIcons = [
      'arrow-left', 'arrow-right',
      'chevron-left', 'chevron-right',
      'caret-left', 'caret-right',
      'arrow-bar-left', 'arrow-bar-right',
    ];
    return directionalIcons.some(icon => iconName.includes(icon));
  }, []);

  return {
    direction,
    language,
    isRTL: direction === 'rtl',
    setLanguage,
    shouldFlipIcon,
  };
}
